// 3rd
import axios from 'axios';
import Vue from 'vue';
import Vuex from 'vuex';
import VuexORM from '@vuex-orm/core';
import moment from 'moment';

// utils
import { i18n } from '../plugins/vuetify';
import { api, authHeaders, errorHandler } from '../utils/api';
import {
    checkNotifications,
    deleteUserRelatedData,
    redirect
} from '../utils/helper';
import {
    LOCAL_STORAGE_ENDPOINT,
    LOCAL_STORAGE_DATA_INVALIDATION,
    LOCAL_STORAGE_JWT,
    LOCAL_STORAGE_USER,
    ROUTE_APPROVALS,
    ROUTE_STATIC_PAGE
} from '../utils/constants';
// modules
import confirmation from './modules/confirmation';
// models
import { User, Profile } from '../models';
import ParticipantApproval from '../models/ParticipantApproval';
import Contract from '../models/Contract';
import ContractType from '../models/ContractType';
import Transaction from '../models/Transaction';
import Product from '../models/Product';
import ProductCategory from '../models/ProductCategory';
import Rank from '../models/Rank';
import Wallet from '../models/Wallet';
import HeroNews from '../models/HeroNews';
import News from '../models/News';
import NewsCategory from '../models/NewsCategory';
import Brand from '../models/Brand';
import WishlistEntry from '../models/WishlistEntry';
import StaticPage from '../models/StaticPage';
import Company from '../models/Company';
import Achievement from '../models/Achievement';
import Cart from '../models/Cart';
import OwnerStatistic from '../models/OwnerStatistic';
import env from '../utils/env';
import Challenge from '../models/Challenge';
import Order from '../models/Order';
import CompletedOrder from '../models/CompletedOrder';
import { SplashScreen } from '@capacitor/splash-screen';
Vue.use(Vuex);

// create a new database instance.
const database = new VuexORM.Database();

// register Models to the database.
database.register(User);
database.register(ParticipantApproval);
database.register(Contract);
database.register(Profile);
database.register(ContractType);
database.register(Transaction);
database.register(Product);
database.register(ProductCategory);
database.register(Rank);
database.register(Wallet);
database.register(HeroNews);
database.register(News);
database.register(NewsCategory);
database.register(Brand);
database.register(WishlistEntry);
database.register(StaticPage);
database.register(Company);
database.register(Achievement);
database.register(Cart);
database.register(OwnerStatistic);
database.register(Challenge);
database.register(Order);
database.register(CompletedOrder);

export default new Vuex.Store({
    namespaced: true,
    plugins: [VuexORM.install(database)],
    modules: {
        confirmation
    },
    state: {
        // layout
        showTwoFactor: false,
        showLoginForm: false,
        appSidebar: false,
        showUnavailableScreen: false,
        endpoint: localStorage.getItem(LOCAL_STORAGE_ENDPOINT),

        // filters
        filterCompletedOrder: null,
        filterNewsCategory: null,
        filterNewsBrand: [],
        filterShopCategory: null,
        filterShopPoint: null,
        filterShopRank: [],
        filterShopNew: [],
        filterAchievementTab: 0,
        filterStatisticsVehicles: null,
        filterStatisticsBrands: null,
        filterStatisticsYears: [],
        filterStatisticUserBrands: 'all',
        filterStatisticsUserYears: [],

        // utils
        tours: false,
        tourIntro: false,
        tourDashboard: false,
        tourShop: false,
        countdown: false,
        init: false,
        bootstrap: false,
        cacheInvalidation: false,
        genders: [],
        companies: [],
        employmentTypes: [],
        locales: [],
        countryCodes: [],
        referer: null,
        refererY: 0,
        contactTopics: [],
        orderReasons: [],
        snackbar: {},
        shopEnabled: false,
        shopUserWaitingPeriod: 3,
        showCheckoutInfo: 0,
        showCheckoutAlert: 0,
        checkoutInfoContent: '',
        checkoutAlertContent: '',
        shopIsDisabledByTerms: false,
        show_taxation_documentation_overlay: false,

        deviceToken: null,
        deviceUuid: null
    },
    mutations: {
        SET(state, { item, value }) {
            state[item] = value;
        },
        SET_BOOLEAN(state, { stateToSet, value }) {
            state[stateToSet] = value;
        },
        SET_INIT(state, init) {
            state.init = init;
        },
        SET_BOOTSTRAP(state, bootstrap) {
            state.bootstrap = bootstrap;
        },
        SET_GENDERS(state, genders) {
            state.genders = genders;
        },
        SET_COMPANIES(state, companies) {
            state.companies = companies;
        },
        SET_EMPLOYMENT_TYPES(state, employmentTypes) {
            state.employmentTypes = employmentTypes;
        },
        SET_LOCALES(state, locales) {
            state.locales = locales;
        },
        SET_REFERER_ROUTE(state, ref) {
            state.referer = ref;
        },
        SET_ORDER_REASONS(state, orderReasons) {
            state.orderReasons = orderReasons;
        },
        SET_CONTACT_TOPICS(state, contactTopics) {
            state.contactTopics = contactTopics;
        },
        SET_SNACKBAR(state, snackbar) {
            state.snackbar = snackbar;
        }
    },
    actions: {
        toggleSidebar({ state, commit }) {
            commit('SET', {
                item: 'appSidebar',
                value: !state.appSidebar
            });
        },
        openSidebar({ state, commit }) {
            commit('SET', {
                item: 'appSidebar',
                value: true
            });
        },
        closeSidebar({ state, commit }) {
            commit('SET', {
                item: 'appSidebar',
                value: false
            });
        },

        openTwoFactorForm({ state, commit }) {
            commit('SET', {
                item: 'showTwoFactor',
                value: true
            });
        },
        closeTwoFactorForm({ state, commit }) {
            commit('SET', {
                item: 'showTwoFactor',
                value: false
            });
        },

        openLoginForm({ state, commit }) {
            commit('SET', {
                item: 'showLoginForm',
                value: true
            });
        },
        closeLoginForm({ state, commit }) {
            commit('SET', {
                item: 'showLoginForm',
                value: false
            });
        },

        set({ commit }, { item, value }) {
            commit('SET', {
                item,
                value
            });
        },

        setFilter({ commit }, { item, value }) {
            commit('SET', {
                item,
                value
            });
        },

        setSnackbar({ commit }, snackbar) {
            commit('SET_SNACKBAR', snackbar);
        },
        setRefererRoute({ commit }, referer) {
            commit('SET_REFERER_ROUTE', referer);
        },
        setBoolean({ commit }, { stateToSet, value }) {
            commit('SET_BOOLEAN', {
                stateToSet,
                value
            });
        },
        setInit({ commit }, init) {
            commit('SET_INIT', init);
        },
        async fetchInit({ state, commit, dispatch }, opt = {}) {
            // options for init action
            const options = {
                skipRedirects: false,
                skipIntro: false,
                impersonate: false,
                ...opt
            };
            // response of init action
            const status = {
                notTriggered: false,
                redirected: false
            };

            // checks if the user already logged in locally
            const locallyLoggedIn =
                !!localStorage.getItem(LOCAL_STORAGE_USER) &&
                !!localStorage.getItem(LOCAL_STORAGE_JWT);

            // request don't need to be triggered anymore
            // init request is already called or user is not logged in locally
            if (state.init || !locallyLoggedIn) {
                status.notTriggered = true;
                await SplashScreen.hide();
                return status;
            }

            const response = await api
                .get(`init`, authHeaders())
                .catch(errorHandler);
            dispatch('setBoolean', { stateToSet: 'init', value: true });
            if (!response) {
                await SplashScreen.hide();
                return;
            }
            deleteUserRelatedData();

            const { data } = response;
            const {
                brands,
                contracts,
                hero_news,
                news,
                latest_transactions,
                news_categories,
                participant_approvals,
                ranks,
                translations,
                user,
                products,
                product_categories,
                wishlist,
                contact_topics,
                ranks_widget,
                cart,
                statistics,
                toast,
                challenges,
                companies,
                order_report_reasons,
                orders,
                settings,
                show_taxation_documentation_overlay
            } = data;

            commit('SET_CONTACT_TOPICS', contact_topics);
            commit('SET_ORDER_REASONS', order_report_reasons);

            if (orders) {
                if (orders.orders) {
                    Order.insert({ data: orders.orders });
                }
                if (orders.completed_orders) {
                    CompletedOrder.insert({ data: orders.completed_orders });
                }
            }
            if (companies) {
                commit('SET_COMPANIES', companies);
            }
            if (brands) {
                Brand.insert({ data: brands });
            }
            if (hero_news) {
                HeroNews.insert({ data: hero_news });
            }
            if (latest_transactions) {
                Transaction.insert({ data: latest_transactions });
            }
            if (news) {
                News.insert({ data: news });
            }
            if (news_categories) {
                NewsCategory.insert({ data: news_categories });
            }
            if (ranks) {
                Rank.insert({ data: ranks });
            }
            if (user) {
                User.insert({ data: user });
            }
            if (ranks_widget) {
                Achievement.insert({ data: ranks_widget });
            }
            if (products) {
                Product.insert({ data: products });
            }
            if (product_categories) {
                ProductCategory.insert({ data: product_categories });
            }
            if (wishlist) {
                WishlistEntry.insert({ data: wishlist });
            }
            if (cart) {
                Cart.insert({ data: cart });
            }
            if (challenges) {
                Challenge.insert({ data: challenges });
            }
            if (toast) {
                dispatch('setSnackbar', {
                    positionX: 'left',
                    type: 'warning',
                    translatedText: toast,
                    time: 5000
                });
            }

            if (statistics) {
                OwnerStatistic.insert({ data: statistics });
            }

            if (show_taxation_documentation_overlay) {
                commit('SET', {
                    item: 'show_taxation_documentation_overlay',
                    value: true
                });
            } else {
                commit('SET', {
                    item: 'show_taxation_documentation_overlay',
                    value: false
                });
            }

            if (settings) {
                // shop settings
                const shopSettings = settings.shop;
                if (shopSettings) {
                    commit('SET', {
                        item: 'shopEnabled',
                        value: shopSettings.shop_enabled
                    });
                    commit('SET', {
                        item: 'shopUserWaitingPeriod',
                        value: shopSettings.user_waiting_period
                    });
                    commit('SET', {
                        item: 'shopIsDisabledByTerms',
                        value: shopSettings.shop_is_disabled_by_terms
                    });
                    commit('SET', {
                        item: 'showCheckoutInfo',
                        value: shopSettings.show_checkout_info
                    });
                    commit('SET', {
                        item: 'showCheckoutAlert',
                        value: shopSettings.show_checkout_alert
                    });
                    commit('SET', {
                        item: 'checkoutInfoContent',
                        value: shopSettings.checkout_info_content
                    });
                    commit('SET', {
                        item: 'checkoutAlertContent',
                        value: shopSettings.checkout_alert_content
                    });
                }
            }

            i18n.locale = user.locale ? user.locale : 'en';

            // add translations
            if (translations) {
                i18n.setLocaleMessage(i18n.locale, {
                    $vuetify: data.translations
                });
            }

            // if there are outstanding approvals, redirect to the approvals page
            if (
                (contracts && contracts.length > 0) ||
                (participant_approvals && participant_approvals.length > 0)
            ) {
                Contract.insert({ data: contracts });
                ParticipantApproval.insert({ data: participant_approvals });
            }

            // set data invalidation
            localStorage.setItem(
                LOCAL_STORAGE_DATA_INVALIDATION,
                moment().unix()
            );

            await checkNotifications();

            // redirect user to the Intro page after login
            // unless checked not to
            const currentUser = User.getCurrent();
            if (
                currentUser &&
                currentUser.intro === false &&
                !options.skipIntro
            ) {
                redirect(ROUTE_STATIC_PAGE, { slug: 'intro' });
                status.redirected = true;
                await SplashScreen.hide();
                return status;
            }

            if (
                (contracts && contracts.length > 0) ||
                (participant_approvals && participant_approvals.length > 0)
            ) {
                if (!options.skipRedirects) {
                    redirect(ROUTE_APPROVALS);
                    status.redirected = true;
                    await SplashScreen.hide();
                    return status;
                }
            }

            await SplashScreen.hide();
            return status;
        },
        async fetchBootstrap({ commit, dispatch }) {
            const response = await api
                .get(`bootstrap/${i18n.locale}`)
                .catch(errorHandler);
            commit('SET_BOOTSTRAP', true);
            if (!response) return;

            const { data } = response;

            let locale = i18n.locale;
            if (data.locale && data.locale !== locale) {
                locale = data.locale;
            }

            i18n.setLocaleMessage(locale, { $vuetify: data.translations });
            commit('SET', {
                item: 'countdown',
                value: data.login_enabled
            });
            commit('SET', {
                item: 'countryCodes',
                value: data.countries.map(c => c.country_code)
            });
            commit('SET_GENDERS', data.genders);
            commit('SET_EMPLOYMENT_TYPES', data.employment_types);
            commit('SET_LOCALES', data.locales ? data.locales : ['en', 'de']);
            StaticPage.insert({ data: data.static_pages });
        }
    }
});
